import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb } from '../../components/Breadcrumb';
import { Modal } from '../../components/Modal';
import { Loader } from '../../components/Loader';
import { AiOutlineFilePdf, AiOutlineFileImage, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFile } from 'react-icons/ai';
import { FiUpload, /* FiTrash2, */ FiDownload, FiGrid, FiList, FiEye } from 'react-icons/fi';
import { fetchFiles, uploadFile, /* deleteFile, */ getFileById, updateFile } from '../../redux/fileStoreStore/action';
import { getUserContext } from '../../utils/auth';
import { FileUploadModal } from '../../components/FileUploadModal';

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '-';
  const units = ['B','KB','MB','GB','TB'];
  let i = 0;
  let b = Math.max(bytes, 0);
  while (b >= 1024 && i < units.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(b < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const getTypeFromName = (name = '') => {
  const n = name.toLowerCase();
  if (/(\.png|\.jpg|\.jpeg|\.gif|\.webp|\.svg)$/.test(n)) return 'image';
  if (/(\.pdf)$/.test(n)) return 'pdf';
  if (/(\.doc|\.docx)$/.test(n)) return 'doc';
  if (/(\.xls|\.xlsx|\.csv)$/.test(n)) return 'sheet';
  return 'other';
};

const normalizeType = (fileType, name = '') => {
  const ft = String(fileType || '').toLowerCase();
  if (ft.startsWith('image/')) return 'image';
  if (ft === 'application/pdf' || ft.endsWith('/pdf')) return 'pdf';
  if (ft.includes('word') || ft === 'application/msword' || ft.includes('officedocument.word')) return 'doc';
  if (ft.includes('excel') || ft.includes('spreadsheet') || ft.endsWith('/csv') || ft.includes('sheet')) return 'sheet';
  return getTypeFromName(name);
};

const TypeIcon = ({ type }) => {
  switch (type) {
    case 'pdf': return <AiOutlineFilePdf className="text-danger" size={20} />;
    case 'image': return <AiOutlineFileImage className="text-info" size={20} />;
    case 'doc': return <AiOutlineFileWord className="text-primary" size={20} />;
    case 'sheet': return <AiOutlineFileExcel className="text-success" size={20} />;
    default: return <AiOutlineFile size={20} />;
  }
};

const FALLBACK_USER_ID = '6897acb7f2bc1479c645102d';
const DEFAULT_BUSINESS_ID = '6897b362dce30c2c9892b759';

export function FileStoreMain() {
  const dispatch = useDispatch();
  const rawContacts = useSelector((s) => s.contact.data);
  const contacts = useMemo(() => rawContacts || [], [rawContacts]);
  const contactsLoading = useSelector((s) => s.contact.status === 'loading');
  const contactIndex = useMemo(() => new Map(contacts.map(c => [c._id, c])), [contacts]);

  const userContext = getUserContext();
  const effectiveUserId = userContext.userId || FALLBACK_USER_ID;
  const effectiveBusinessId = userContext.businessId || DEFAULT_BUSINESS_ID;

  useEffect(() => {
    if (!effectiveUserId) return;
    try {
      const { fetchContacts } = require('../../redux/contactStore/action');
      dispatch(fetchContacts({
        userId: effectiveUserId,
        businessId: effectiveBusinessId,
      }));
    } catch (e) {
      // noop
    }
  }, [dispatch, effectiveUserId, effectiveBusinessId]);

  const rawFiles = useSelector((s) => s.fileStore.data);
  const files = useMemo(() => rawFiles || [], [rawFiles]);
  const filesStatus = useSelector((s) => s.fileStore.status);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [openTooltipId, setOpenTooltipId] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState({ id: null, fileName: '', contactIds: [] });
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [preview, setPreview] = useState({ open: false, url: '', type: '', name: '' });
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  useEffect(() => {
    if (openTooltipId == null) return;
    const onClickAway = () => setOpenTooltipId(null);
    document.addEventListener('click', onClickAway);
    return () => document.removeEventListener('click', onClickAway);
  }, [openTooltipId]);

  // Fetch files when filters/search change (debounced)
  useEffect(() => {
    if (!effectiveUserId) return;
    const t = setTimeout(() => {
      dispatch(fetchFiles({
        search,
        type: typeFilter,
        userId: effectiveUserId,
        businessId: effectiveBusinessId,
      }));
    }, 200);
    return () => clearTimeout(t);
  }, [dispatch, search, typeFilter, effectiveUserId, effectiveBusinessId]);

  const filtered = useMemo(() => {
    let list = files.map(f => ({
      id: f._id || f.id,
      name: f.fileName || f.name,
      size: f.fileSize ?? f.size,
      uploadedAt: f.createdAt || f.uploadedAt,
      type: normalizeType(f.fileType || f.type, f.fileName || f.name),
      contacts: f.contactIds || f.contacts || [],
    }));
    if (typeFilter !== 'all') list = list.filter(f => f.type === typeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const contactIndex = new Map(contacts.map(c => [c._id, c]));
      list = list.filter(f => {
        const nameMatch = f.name.toLowerCase().includes(q);
        if (nameMatch) return true;
        // Check associated contacts
        const ids = Array.isArray(f.contacts) ? f.contacts : [];
        for (const id of ids) {
          const c = contactIndex.get(id);
          if (!c) continue;
          const fields = [c.contactName, c.contactEmail, c.contactPhone];
          if (fields.some(v => (v || '').toLowerCase().includes(q))) return true;
        }
        return false;
      });
    }
    return list;
  }, [files, typeFilter, search, contacts]);

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleUpload = async () => {
    try {
      setIsLoading(true);
      if (!effectiveUserId || !effectiveBusinessId) {
        throw new Error('Missing user or business context for upload.');
      }
      for (const f of selectedFiles) {
        const base64 = await toBase64(f);
        await dispatch(uploadFile({
          userId: effectiveUserId,
          businessId: effectiveBusinessId,
          fileName: f.name,
          fileType: f.type,
          fileSize: f.size,
          dataBase64: base64,
          contactIds: selectedContactIds,
        })).unwrap();
      }
      setSelectedFiles([]);
      setSelectedContactIds([]);
      setShowUpload(false);
      // refresh list with current search/type
      dispatch(fetchFiles({
        search,
        type: typeFilter,
        userId: effectiveUserId,
        businessId: effectiveBusinessId,
      }));
    } catch (e) {
      console.error('Upload failed', e);
    } finally {
      setIsLoading(false);
    }
  };

  /* const handleDelete = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    await dispatch(deleteFile(id));
    dispatch(fetchFiles({
      search,
      type: typeFilter,
      userId: effectiveUserId,
      businessId: effectiveBusinessId,
    }));
  }; */

  const handleDownload = async (id, name, mime) => {
    try {
      const res = await dispatch(getFileById(id)).unwrap();
      const downloadUrl = res.downloadUrl || res.fileUrl;
      if (!downloadUrl) throw new Error('File download is not available');
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener';
      link.download = name || res.fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Download failed', e);
    }
  };

  const handlePreview = async (file) => {
    try {
      setIsPreviewLoading(true);
      setPreview(prev => ({ ...prev, open: true, url: '', type: file.type, name: file.name }));
      const res = await dispatch(getFileById(file.id)).unwrap();
      const url = res.downloadUrl || res.fileUrl;
      if (!url) throw new Error('Preview not available');
      setPreview({ open: true, url, type: file.type, name: file.name });
    } catch (e) {
      console.error('Preview failed', e);
      setPreview({ open: true, url: '', type: file.type, name: file.name });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const openEdit = (file) => {
    const ids = Array.isArray(file.contacts) ? file.contacts : [];
    setEditDraft({ id: file.id, fileName: file.name, contactIds: ids });
    setContactSearch('');
    setSelectedContactIds(ids);
    setIsEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      await dispatch(updateFile({ id: editDraft.id, data: { fileName: editDraft.fileName, contactIds: selectedContactIds } })).unwrap();
      setIsEditOpen(false);
      setSelectedContactIds([]);
      dispatch(fetchFiles({
        search,
        type: typeFilter,
        userId: effectiveUserId,
        businessId: effectiveBusinessId,
      }));
    } catch (e) {
      console.error('Update failed', e);
    }
  };

  const badgeForType = (t) => {
    switch (t) {
      case 'pdf': return 'danger';
      case 'image': return 'info';
      case 'doc': return 'primary';
      case 'sheet': return 'success';
      default: return 'secondary';
    }
  };

  return (
    <div className="container">
      <div className="d-flex align-items-center justify-content-between">
        <Breadcrumb items={[{label:'Home', path:'/dashboard'}, {label:'File Store'}]} />
        {(isLoading || filesStatus === 'loading') ? <Loader size="sm" /> : null}
      </div>

      <div className="row align-items-end g-2 mb-3">
        <div className="col-md-8">
          <div className="d-flex flex-column flex-sm-row gap-2">
            <div className="flex-grow-1">
              <label htmlFor="fileSearch" className="visually-hidden">Search files</label>
              <input id="fileSearch" className="form-control" placeholder="Search files" value={search} onChange={(e)=> setSearch(e.target.value)} />
            </div>
            <div style={{ minWidth: 160 }}>
              <label htmlFor="typeFilter" className="visually-hidden">Filter by type</label>
              <select id="typeFilter" className="form-select" value={typeFilter} onChange={(e)=> setTypeFilter(e.target.value)}>
                <option value="all">All types</option>
                <option value="image">Images</option>
                <option value="pdf">PDF</option>
                <option value="doc">Documents</option>
                <option value="sheet">Sheets</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="d-flex justify-content-md-end gap-2">
            <button className="btn btn-primary" onClick={()=> setShowUpload(true)}>
              <FiUpload className="me-1" /> Upload
            </button>
            <div className="btn-group" role="group" aria-label="View toggle">
              <button
                className={`btn btn-outline-secondary ${viewMode === 'list' ? 'active' : ''}`}
                title="List view"
                onClick={() => setViewMode('list')}
              >
                <FiList />
              </button>
              <button
                className={`btn btn-outline-secondary ${viewMode === 'grid' ? 'active' : ''}`}
                title="Grid view"
                onClick={() => setViewMode('grid')}
              >
                <FiGrid />
              </button>
            </div>
            {/* Placeholder for future features
            <button className="btn btn-outline-secondary">New Folder</button> */}
          </div>
        </div>
      </div>

      {viewMode === 'list' && (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th className="text-end">Size</th>
                <th>Uploaded</th>
                <th className="text-end">Contacts</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(f => (
                <tr key={f.id}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <TypeIcon type={f.type} />
                      <span className="fw-medium">{f.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`badge bg-${badgeForType(f.type)}`}>{f.type}</span>
                  </td>
                  <td className="text-end">{formatSize(f.size)}</td>
                  <td>{new Date(f.uploadedAt).toLocaleString()}</td>
                  <td className="text-end">
                    <div className="d-inline-block position-relative">
                      {(() => {
                        const ids = Array.isArray(f.contacts) ? f.contacts : [];
                        const labels = ids.map(id => {
                          const c = contactIndex.get(id);
                          const label = c?.contactName || c?.contactPhone || c?.contactEmail || String(id).slice(0,6);
                          return label;
                        });
                        if (labels.length === 0) return <span className="text-muted">-</span>;
                        const first = labels[0];
                        const more = labels.length - 1;
                        return (
                          <>
                            <span className="me-1">{first}</span>
                            {more > 0 && (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 align-baseline"
                                onClick={(e) => { e.stopPropagation(); setOpenTooltipId(openTooltipId === f.id ? null : f.id); }}
                              >
                                +{more} more
                              </button>
                            )}
                            {openTooltipId === f.id && (
                              <div
                                className="card shadow-sm"
                                style={{ position: 'absolute', right: 0, top: '120%', zIndex: 1050, minWidth: 220, maxWidth: '90vw' }}
                                onClick={(e)=> e.stopPropagation()}
                              >
                                <div className="card-body py-2">
                                  <div className="small">
                                    {labels.map((name, idx) => (
                                      <div key={idx}>{name}</div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-secondary me-2" title="Preview" onClick={()=> handlePreview(f)}><FiEye /></button>
                    <button className="btn btn-sm btn-outline-secondary me-2" title="Download" onClick={()=> handleDownload(f.id, f.name, f.type)}><FiDownload /></button>
                    <button className="btn btn-sm btn-outline-info me-2" title="Edit" onClick={()=> openEdit(f)}>Edit</button>
                    {/* <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={()=> handleDelete(f.id)}><FiTrash2 /></button> */}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">No files found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === 'grid' && (
        <div className="row g-3">
          {filtered.map(f => (
            <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={f.id}>
              <div className="card h-100 shadow-sm">
                <div className="card-body p-2 d-flex flex-column align-items-center text-center">
                  <div className="mb-2"><TypeIcon type={f.type} /></div>
                  <div className="small text-truncate w-100" title={f.name}>{f.name}</div>
                  <div className="text-muted small">{formatSize(f.size)}</div>
                  <div className="text-muted small">{new Date(f.uploadedAt).toLocaleDateString()}</div>
                </div>
                <div className="card-footer p-2 d-flex justify-content-center gap-1">
                  <button className="btn btn-sm btn-outline-secondary" title="Preview" onClick={()=> handlePreview(f)}><FiEye /></button>
                  <button className="btn btn-sm btn-outline-secondary" title="Download" onClick={()=> handleDownload(f.id, f.name, f.type)}><FiDownload /></button>
                  <button className="btn btn-sm btn-outline-info" title="Edit" onClick={()=> openEdit(f)}>Edit</button>
                  {/* <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={()=> handleDelete(f.id)}><FiTrash2 /></button> */}
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-12 text-center text-muted py-4">No files found.</div>
          )}
        </div>
      )}

      <FileUploadModal
        show={showUpload}
        onClose={() => {
          if (isLoading) return;
          setShowUpload(false);
          setSelectedFiles([]);
          setSelectedContactIds([]);
          setContactSearch('');
        }}
        title="Upload Files"
        helperText="Select one or more files (PDF, images, docs, etc.)."
        selectedFiles={selectedFiles}
        onFilesChange={setSelectedFiles}
        primaryActionLabel={isLoading ? 'Uploading...' : 'Upload'}
        primaryDisabled={isLoading || selectedFiles.length === 0}
        isUploading={isLoading}
        onPrimaryAction={handleUpload}
      >
        <div className="mb-3">
          <label className="form-label">Associate Contacts (optional)</label>
          <div className="d-flex gap-2 mb-2">
            <input
              className="form-control form-control-sm"
              placeholder="Search contacts by name, email or phone"
              value={contactSearch}
              onChange={(e)=> setContactSearch(e.target.value)}
            />
          </div>
          <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {contactsLoading && <Loader />}
            {!contactsLoading && contacts
              .filter(c => {
                if (!contactSearch.trim()) return true;
                const q = contactSearch.trim().toLowerCase();
                return [c.contactName, c.contactEmail, c.contactPhone]
                  .some(v => (v || '').toLowerCase().includes(q));
              })
              .map(c => {
                const id = c._id;
                const checked = selectedContactIds.includes(id);
                const label = c.contactName || c.contactPhone || c.contactEmail || id;
                return (
                  <div className="form-check" key={id}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`assoc_${id}`}
                      checked={checked}
                      onChange={() => {
                        setSelectedContactIds(prev => checked ? prev.filter(x => x !== id) : [...prev, id]);
                      }}
                    />
                    <label className="form-check-label" htmlFor={`assoc_${id}`}>{label}</label>
                  </div>
                );
              })}
            {!contactsLoading && contacts.length === 0 && (
              <div className="text-muted">No contacts found.</div>
            )}
          </div>
          {selectedContactIds.length > 0 && (
            <div className="form-text">Selected: {selectedContactIds.length} contact(s)</div>
          )}
        </div>
      </FileUploadModal>

      {/* Preview Modal */}
      <Modal
        show={preview.open}
        onClose={() => setPreview({ open: false, url: '', type: '', name: '' })}
        title={`Preview${preview.name ? ` - ${preview.name}` : ''}`}
        dialogClassName="modal-xl"
        body={
          <div>
            {isPreviewLoading && <Loader />}
            {!isPreviewLoading && (
              preview.url ? (
                preview.type === 'image' ? (
                  <div className="text-center">
                    <img src={preview.url} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
                  </div>
                ) : preview.type === 'pdf' ? (
                  <iframe title="preview" src={preview.url} style={{ width: '100%', minHeight: '70vh', border: 0 }} />
                ) : (
                  <div className="text-center text-muted">
                    Preview not available for this file type.
                  </div>
                )
              ) : (
                <div className="text-center text-muted">Preview not available.</div>
              )
            )}
          </div>
        }
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setPreview({ open: false, url: '', type: '', name: '' })}>Close</button>
            {preview.url && (
              <button className="btn btn-primary" onClick={() => {
                // Prefer opening in a new tab for download
                const link = document.createElement('a');
                link.href = preview.url;
                link.target = '_blank';
                link.rel = 'noopener';
                link.download = preview.name || 'download';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}>Download</button>
            )}
          </>
        }
      />

      {/* Edit File Modal */}
      <Modal
        show={isEditOpen}
        onClose={()=> setIsEditOpen(false)}
        title="Edit File"
        body={
          <div>
            <div className="mb-3">
              <label htmlFor="editFileName" className="form-label">File name</label>
              <input
                id="editFileName"
                className="form-control"
                value={editDraft.fileName}
                onChange={(e)=> setEditDraft(prev => ({ ...prev, fileName: e.target.value }))}
              />
            </div>
            <div className="mb-2">
              <label className="form-label">Associated Contacts</label>
              <div className="d-flex gap-2 mb-2">
                <input
                  className="form-control form-control-sm"
                  placeholder="Search contacts"
                  value={contactSearch}
                  onChange={(e)=> setContactSearch(e.target.value)}
                />
              </div>
              <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {contactsLoading && <Loader />}
                {!contactsLoading && contacts
                  .filter(c => {
                    if (!contactSearch.trim()) return true;
                    const q = contactSearch.trim().toLowerCase();
                    return [c.contactName, c.contactEmail, c.contactPhone].some(v => (v || '').toLowerCase().includes(q));
                  })
                  .map(c => {
                    const id = c._id;
                    const checked = selectedContactIds.includes(id);
                    const label = c.contactName || c.contactPhone || c.contactEmail || id;
                    return (
                      <div className="form-check" key={id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`edit_assoc_${id}`}
                          checked={checked}
                          onChange={() => {
                            setSelectedContactIds(prev => checked ? prev.filter(x => x !== id) : [...prev, id]);
                          }}
                        />
                        <label className="form-check-label" htmlFor={`edit_assoc_${id}`}>{label}</label>
                      </div>
                    );
                  })}
                {!contactsLoading && contacts.length === 0 && (
                  <div className="text-muted">No contacts found.</div>
                )}
              </div>
              {selectedContactIds.length > 0 && (
                <div className="form-text">Selected: {selectedContactIds.length} contact(s)</div>
              )}
            </div>
          </div>
        }
        footer={
          <>
            <button className="btn btn-secondary" onClick={()=> setIsEditOpen(false)}>Close</button>
            <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
          </>
        }
      />
    </div>
  );
}
